import { LineItem } from 'types/Sale';

/**
 * Centralized line item validation utilities to ensure consistency across all invoice components
 */

/**
 * Determines if a line item should be displayed in the UI
 * This is the complete validation logic that matches what InvoiceItemsList actually renders
 *
 * @param lineItem - The line item to check
 * @returns true if the item should be displayed, false otherwise
 */
export const isLineItemDisplayable = (lineItem: LineItem): boolean => {
  // Must have valid item object
  if (!lineItem?.item) {
    return false;
  }

  // Must have displayable description (either item.name or lineItem.description)
  const hasDisplayableDescription = (lineItem.item && lineItem.item.name) || lineItem.description;

  return !!hasDisplayableDescription;
};

/**
 * Filters an array of line items to only those that will be displayed in the UI
 * Use this instead of manual filtering to ensure consistency
 *
 * @param lineItems - Array of line items to filter
 * @returns Filtered array containing only displayable line items
 */
export const getDisplayableLineItems = (lineItems?: LineItem[]): LineItem[] => {
  if (!lineItems) return [];
  return lineItems.filter(isLineItemDisplayable);
};

/**
 * Gets the count of line items that will actually be displayed
 * Use this for analytics and display logic
 *
 * @param lineItems - Array of line items to count
 * @returns Number of items that will be displayed
 */
export const getDisplayableLineItemsCount = (lineItems?: LineItem[]): number => {
  return getDisplayableLineItems(lineItems).length;
};

/**
 * Legacy validation function used in InvoiceLineItems.tsx
 * Consider migrating to isLineItemDisplayable for complete accuracy
 *
 * @param lineItem - The line item to check
 * @returns true if item is not null, false otherwise
 */
export const isLineItemNotNull = (lineItem: LineItem): boolean => {
  return lineItem?.item !== null;
};
